import { RouteObject, redirect } from 'react-router-dom';
import ErrorPage from 'app/error';
import {
  ProtectedLayout,
  AuthLayout,
  AuthPagesLayout,
  ProtectedLayoutWithChildren,
} from 'components/layouts';
import { store } from './store';
import { MedicinesProvider } from 'store/context/medicines';
import { commonService } from 'services/common';
import Maintenance from 'app/maintenance';
import constants from 'utils/constants';

const commonPages = [
  {
    path: 'get-info',
    async lazy() {
      const Component = (await import('app/common/get-info')).default as ChildrenRoute;
      return { element: <Component title="get-info" /> };
    },
  },
  {
    path: 'kvkk',
    async lazy() {
      const Component = (await import('app/common/kvkk')).default as ChildrenRoute;
      return { element: <Component title="kvkk" /> };
    },
  },
  {
    path: 'contact',
    async lazy() {
      const Component = (await import('app/common/contact')).default as ChildrenRoute;
      return { element: <Component title="contact-us" /> };
    },
  },
  {
    path: 'faq',
    async lazy() {
      const Component = (await import('app/common/faq')).default as ChildrenRoute;
      return { element: <Component title="faq" /> };
    },
  },
  {
    path: 'qr',
    async lazy() {
      const Component = (await import('app/common/qr')).default as ChildrenRoute;
      return { element: <Component title="mobile-app" /> };
    },
  },
];

// maintenance mode check
const maintenanceLoader = async () => {
  const response = await store
    .dispatch(
      commonService.endpoints.applicationInfo.initiate({
        applicationCode: constants.applicationCode,
      })
    )
    .unwrap();

  if (response?.data?.maintenanceMode) {
    throw redirect('/maintenance');
  }
  return null;
};

export const MaintenanceRoute: RouteObject[] = [
  {
    path: '/maintenance',
    element: <Maintenance />,
    loader: async () => {
      const response = await store
        .dispatch(
          commonService.endpoints.applicationInfo.initiate({
            applicationCode: constants.applicationCode,
          })
        )
        .unwrap();

      if (!response.data.maintenanceMode) {
        throw redirect('/');
      }
      return null;
    },
    errorElement: <ErrorPage />,
  },
];

export const ProtectedRoutes: RouteObject[] = [
  {
    path: '/',
    element: <ProtectedLayout />,
    loader: async () => {
      // first check maintenance status
      await maintenanceLoader();

      // then check auth status
      const { user } = store.getState().auth;
      if (!user) {
        throw redirect('/login');
      }
      return null;
    },
    errorElement: <ErrorPage />,
    children: [
      // {
      //   path: 'dev',
      //   async lazy() {
      //     const Component = (await import('app/dev')).default as ChildrenRoute;
      //     return { element: <Component title="test" /> };
      //   },
      // },
      {
        index: true,
        // Do not define path if index true
        // path: 'dashboard',
        async lazy() {
          const Component = (await import('app/dashboard')).default as ChildrenRoute;
          return { element: <Component title="home-page" /> };
        },
        // async lazy() {
        //   const Component = (await import('app/dashboard')).default;
        //   return { Component };
        // },
        // lazy: async () => ({ Component: (await import('app/dashboard')).default }),
      },
      {
        path: 'health-knowledge',
        async lazy() {
          const Component = (await import('app/health-knowledge')).default as ChildrenRoute;
          return { element: <Component title="health-knowledge" /> };
        },
      },
      {
        path: 'departments',
        async lazy() {
          const Component = (await import('app/departments')).default as ChildrenRoute;
          return { element: <Component title="departments" /> };
        },
      },
      {
        path: 'doctors',
        async lazy() {
          const Component = (await import('app/doctors')).default as ChildrenRoute;
          return { element: <Component title="doctors" /> };
        },
      },
      {
        element: <ProtectedLayoutWithChildren />,
        path: 'appointments',
        children: [
          {
            index: true,
            async lazy() {
              const Component = (await import('app/appointments')).default as ChildrenRoute;
              return { element: <Component title="appointments" /> };
            },
          },
          {
            path: 'book-appointment',
            async lazy() {
              const Component = (await import('app/appointments/book-appointment'))
                .default as ChildrenRoute;
              return { element: <Component title="book-appointment" /> };
            },
          },
        ],
      },
      {
        element: <ProtectedLayoutWithChildren />,
        path: 'insurances',
        children: [
          {
            index: true,
            async lazy() {
              const Component = (await import('app/insurances')).default as ChildrenRoute;
              return { element: <Component title="insurances" /> };
            },
          },
        ],
      },
      {
        element: <ProtectedLayoutWithChildren />,
        path: 'medicines',
        children: [
          {
            index: true,
            async lazy() {
              const Component = (await import('app/medicines')).default as ChildrenRoute;
              return {
                element: (
                  <MedicinesProvider>
                    <Component title="medicine-reminder" />
                  </MedicinesProvider>
                ),
              };
            },
          },
          {
            path: 'add-medicine',
            async lazy() {
              const Component = (await import('app/medicines/add')).default as ChildrenRoute;
              return { element: <Component title="add-new-medicine" /> };
            },
          },
          {
            path: 'edit-medicine',
            async lazy() {
              const Component = (await import('app/medicines/edit')).default as ChildrenRoute;
              return { element: <Component title="edit-medicine" /> };
            },
          },
        ],
      },
      {
        element: <ProtectedLayoutWithChildren />,
        path: 'reports',
        children: [
          {
            index: true,
            async lazy() {
              const Component = (await import('app/reports')).default as ChildrenRoute;
              return { element: <Component title="reports" /> };
            },
          },
          {
            element: <ProtectedLayoutWithChildren />,
            path: 'types',
            children: [
              {
                index: true,
                async lazy() {
                  const Component = (await import('app/reports/types')).default as ChildrenRoute;
                  return { element: <Component title="reports" /> };
                },
              },
              {
                path: ':id',
                async lazy() {
                  const Component = (await import('app/reports/[id]')).default as ChildrenRoute;
                  return { element: <Component title="reports" /> };
                },
              },
            ],
          },
        ],
      },
      // {
      //   path: 'pet-hairdresser',
      //   async lazy() {
      //     const Component = (await import('app/hairdresser')).default as ChildrenRoute;
      //     return { element: <Component title="hairdresser" /> };
      //   },
      // },
      {
        path: 'hospital',
        async lazy() {
          const Component = (await import('app/hospital')).default as ChildrenRoute;
          return { element: <Component title="hospital" /> };
        },
      },
      {
        element: <ProtectedLayoutWithChildren />,
        path: 'visits',
        children: [
          {
            index: true,
            async lazy() {
              const Component = (await import('app/visits')).default as ChildrenRoute;
              return { element: <Component title="visits" /> };
            },
          },
          {
            element: <ProtectedLayoutWithChildren />,
            path: 'types',
            children: [
              {
                index: true,
                async lazy() {
                  const Component = (await import('app/visits/types')).default as ChildrenRoute;
                  return { element: <Component title="visits" /> };
                },
              },
              {
                path: ':id',
                async lazy() {
                  const Component = (await import('app/visits/[id]')).default as ChildrenRoute;
                  return { element: <Component title="visits" /> };
                },
              },
            ],
          },
        ],
      },
      {
        element: <ProtectedLayoutWithChildren />,
        path: 'vaccines',
        children: [
          {
            index: true,
            async lazy() {
              const Component = (await import('app/vaccines')).default as ChildrenRoute;
              return { element: <Component title="vaccine-monitoring" /> };
            },
          },
          {
            path: ':id',
            async lazy() {
              const Component = (await import('app/vaccines/[id]')).default as ChildrenRoute;
              return { element: <Component title="vaccine-monitoring" /> };
            },
          },
        ],
      },
      {
        element: <ProtectedLayoutWithChildren />,
        path: 'files',
        children: [
          {
            index: true,
            async lazy() {
              const Component = (await import('app/files')).default as ChildrenRoute;
              return { element: <Component title="files" /> };
            },
          },
          {
            element: <ProtectedLayoutWithChildren />,
            path: ':petId',
            children: [
              {
                index: true,
                async lazy() {
                  const Component = (await import('app/files/[petId]')).default as ChildrenRoute;
                  return { element: <Component title="files" /> };
                },
              },
              {
                path: 'file',
                async lazy() {
                  const Component = (await import('app/files/file')).default as ChildrenRoute;
                  return { element: <Component title="files" /> };
                },
              },
              // {
              //   path: 'edit',
              //   async lazy() {
              //     const Component = (await import('app/files/detail')).default as ChildrenRoute;
              //     return { element: <Component title="files" /> };
              //   },
              // },
            ],
          },
        ],
      },
      {
        element: <ProtectedLayoutWithChildren />,
        path: 'pet-mates',
        children: [
          {
            index: true,
            async lazy() {
              const Component = (await import('app/pet-mates')).default as ChildrenRoute;
              return { element: <Component title="my-pet-mates" /> };
            },
          },
          {
            path: ':id',
            element: <ProtectedLayoutWithChildren />,
            children: [
              {
                index: true,
                async lazy() {
                  const Component = (await import('app/pet-mates/[id]')).default as ChildrenRoute;
                  return { element: <Component title="pet-mate" /> };
                },
              },
              {
                path: 'edit',
                async lazy() {
                  const Component = (await import('app/pet-mates/edit')).default as ChildrenRoute;
                  return { element: <Component title="edit-pet-mate" /> };
                },
              },
            ],
          },
          {
            path: 'add',
            async lazy() {
              const Component = (await import('app/pet-mates/add')).default as ChildrenRoute;
              return { element: <Component title="add-pet-mate" /> };
            },
          },
          {
            path: 'passed-away',
            async lazy() {
              const Component = (await import('app/pet-mates/passed-away')).default as ChildrenRoute;
              return { element: <Component title="passed-away" /> };
            },
          },
        ],
      },
      {
        element: <ProtectedLayoutWithChildren />,
        path: 'checkout',
        children: [
          {
            index: true,
            async lazy() {
              const Component = (await import('app/checkout/basket')).default as ChildrenRoute;
              return { element: <Component title="basket" /> };
            },
          },

          {
            path: 'payment',
            async lazy() {
              const Component = (await import('app/checkout/payment')).default as ChildrenRoute;
              return { element: <Component title="payment" /> };
            },
          },
        ],
      },
      {
        element: <ProtectedLayoutWithChildren />,
        path: 'account',
        children: [
          {
            index: true,
            async lazy() {
              const Component = (await import('app/account')).default as ChildrenRoute;
              return { element: <Component title="my-account" /> };
            },
          },
        ],
      },
      {
        element: <ProtectedLayoutWithChildren />,
        path: 'membership-programs',
        children: [
          {
            index: true,
            async lazy() {
              const Component = (await import('app/membership-programs')).default as ChildrenRoute;
              return { element: <Component title="membership-programs" /> };
            },
          },
          {
            element: <ProtectedLayoutWithChildren />,
            path: 'programs',
            children: [
              {
                index: true,
                async lazy() {
                  const Component = (await import('app/membership-programs/programs'))
                    .default as ChildrenRoute;
                  return { element: <Component title="membership-programs" /> };
                },
              },
              {
                element: <ProtectedLayoutWithChildren />,
                path: 'plans',
                children: [
                  {
                    index: true,
                    async lazy() {
                      const Component = (await import('app/membership-programs/plans'))
                        .default as ChildrenRoute;
                      return { element: <Component title="membership-programs" /> };
                    },
                  },
                  {
                    path: 'health-expenditure',
                    async lazy() {
                      const Component = (await import('app/membership-programs/health-expenditure'))
                        .default as ChildrenRoute;
                      return { element: <Component title="membership-programs" /> };
                    },
                  },
                ],
              },
            ],
          },
        ],
      },
      {
        element: <ProtectedLayoutWithChildren />,
        path: 'membership-purchase',
        children: [
          {
            index: true,
            async lazy() {
              const Component = (await import('app/membership-purchase')).default as ChildrenRoute;
              return { element: <Component title="membership-purchase" /> };
            },
          },
          {
            element: <ProtectedLayoutWithChildren />,
            path: 'plans',
            children: [
              {
                index: true,
                async lazy() {
                  const Component = (await import('app/membership-purchase/plans'))
                    .default as ChildrenRoute;
                  return { element: <Component title="membership-purchase" /> };
                },
              },
              {
                element: <ProtectedLayoutWithChildren />,
                path: 'plan-detail',
                children: [
                  {
                    index: true,
                    async lazy() {
                      const Component = (await import('app/membership-purchase/plan-detail'))
                        .default as ChildrenRoute;
                      return { element: <Component title="membership-purchase" /> };
                    },
                  },
                ],
              },
            ],
          },
        ],
      },
      {
        path: 'common',
        errorElement: <ErrorPage />,
        loader: async (p) => {
          const { user } = store.getState().auth;

          if (!user) {
            throw redirect('/login');
          }
          return null;
        },
        children: [...commonPages],
      },
    ],
  },
];

export const AuthRoutes: RouteObject[] = [
  {
    path: '/',
    element: <AuthLayout />,
    loader: async () => {
      await maintenanceLoader();

      const { user } = store.getState().auth;
      if (user) {
        throw redirect('/');
      }
      return null;
    },
    errorElement: <ErrorPage />,
    children: [
      {
        path: 'login',
        async lazy() {
          const Component = (await import('app/auth/login')).default;
          return { Component };
        },
      },
      {
        path: 'register',
        async lazy() {
          const Component = (await import('app/auth/register')).default;
          return { Component };
        },
      },
    ],
  },
  {
    element: <AuthPagesLayout />,
    errorElement: <ErrorPage />,
    loader: async (p) => {
      const { user } = store.getState().auth;

      if (user) {
        const { pathname, search } = new URL(p.request.url);
        const commonPage = commonPages.find((page) => page.path === pathname.slice(1));
        if (commonPage) {
          throw redirect(`/common${pathname}${search}`);
        } else {
          throw redirect('/');
        }
      }
      return null;
    },
    children: [
      {
        path: 'help',
        async lazy() {
          const Component = (await import('app/common/help')).default as ChildrenRoute;
          return { element: <Component title="online-help" /> };
        },
      },
      ...commonPages,
    ],
  },
];

